import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Observable, Subject, of, forkJoin } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { TaskDetailModalComponent } from '../task-detail-modal/task-detail-modal.component';
import { PickerOption } from '../../../shared/components/form/entity-picker/entity-picker.component';
import { TaskFiltersComponent } from '../shared/task-filters/task-filters.component';
import { TaskDrawerService } from '../services/task-drawer.service';
import { TaskApiService } from '../services/task-api.service';
import { TaskCatalogApiService } from '../services/task-catalog-api.service';
import { TaskErrorService } from '../services/task-error.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import {
  TaskResponse,
  TaskFilterRequest,
  TaskStatusResponse,
  TaskPriorityResponse,
  TaskTypeResponse,
  TaskCategoryResponse,
  TaskUpdateRequest,
  TaskFilterEvent,
  QuickFilter,
  TaskCancelReasonResponse,
} from '../models/task.model';
import { TaskCancelDialogComponent } from '../shared/task-cancel-dialog/task-cancel-dialog.component';
import { ToastService } from '../../../shared/services/toast.service';
import { TaskCompleteDialogComponent } from '../shared/task-complete-dialog/task-complete-dialog.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type ViewMode = 'status' | 'priority' | 'type' | 'category';

interface KanbanColumn {
  id: number;
  name: string;
  label: string;
  color: string;
  hexColor: string;
  tasks: TaskResponse[];
}

@Component({
  selector: 'app-tarea-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    TaskDetailModalComponent,
    TaskFiltersComponent,
    TaskCancelDialogComponent,
    TaskCompleteDialogComponent,
  ],
  templateUrl: './tareas-kamban.component.html',
})
export class TareaKambanComponent implements OnInit, OnDestroy {
  titulo = 'Kanban de Tareas';

  viewMode: ViewMode = 'status';
  // Definimos los modos con el tipo correcto para evitar errores de inferencia en el HTML
  readonly availableModes: { v: ViewMode; l: string }[] = [
    { v: 'status', l: 'Estado' },
    { v: 'priority', l: 'Prioridad' },
    { v: 'type', l: 'Tipo' },
    { v: 'category', l: 'Categoría' },
  ];
  columns: KanbanColumn[] = [];
  loading = false;

  // Catálogos para chips de filtro y para construir columnas según viewMode
  statuses: TaskStatusResponse[] = [];
  priorities: TaskPriorityResponse[] = [];
  types: TaskTypeResponse[] = [];
  categories: TaskCategoryResponse[] = [];

  // Filtro actual
  currentFilter: TaskFilterRequest = {};
  quickFilter: QuickFilter = 'all';

  // Paginación
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'tareas-kanban-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 20;
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  // Detail modal state
  showDetailModal = false;
  detailTaskId?: number;

  // Cancel modal
  showCancelDialog = false;
  cancelTargetTask: TaskResponse | null = null;
  cancelReasons: TaskCancelReasonResponse[] = [];

  // Complete modal
  showCompleteDialog = false;
  completeTargetTask: TaskResponse | null = null;

  // Cache de nombres por id (mismo patrón que list)
  private readonly companyNames = new Map<number, string>();
  private readonly dealNames = new Map<number, string>();
  private readonly leadNames = new Map<number, string>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private taskApi: TaskApiService,
    private catalogApi: TaskCatalogApiService,
    private authService: AuthService,
    private drawerService: TaskDrawerService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private leadApi: LeadApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private taskErrorService: TaskErrorService, // Inyectamos el servicio de errores
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadPage(this.globalPage));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarCatalogos(): void {
    this.loading = true;
    Promise.all([
      this.catalogApi.getActiveStatuses().toPromise(),
      this.catalogApi.getActivePriorities().toPromise(),
      this.catalogApi.getActiveTypes().toPromise(),
      this.catalogApi.getActiveCategories().toPromise(),
      this.catalogApi.getActiveCancelReasons().toPromise(),
    ])
      .then(([statuses, priorities, types, categories, cancelReasons]) => {
        this.statuses = [...(statuses || [])].sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
        );
        this.priorities = [...(priorities || [])].sort(
          (a, b) => (a.level ?? 99) - (b.level ?? 99),
        );
        this.types = types || [];
        this.categories = categories || [];
        this.cancelReasons = cancelReasons || [];
        this.initializeColumns();
      })
      .catch(() => {
        this.columns = [];
        this.loading = false;
      });
  }

  private initializeColumns(): void {
    switch (this.viewMode) {
      case 'status':
        this.columns = this.statuses.map((s) => ({
          id: s.id,
          name: s.name,
          label: s.label,
          color: this.colorClassFor(s),
          hexColor: s.color || '#6B7280',
          tasks: [],
        }));
        break;
      case 'priority':
        this.columns = this.priorities.map((p) => ({
          id: p.id,
          name: p.name || `N${p.level}`,
          label: p.label,
          color: 'bg-gray-400',
          hexColor: p.color || '#9CA3AF',
          tasks: [],
        }));
        this.columns.push({
          id: 0,
          name: 'sin',
          label: 'Sin prioridad',
          color: 'bg-gray-300',
          hexColor: '#D1D5DB',
          tasks: [],
        });
        break;
      case 'type':
        this.columns = this.types.map((t) => ({
          id: t.id,
          name: t.name || t.label,
          label: t.label,
          color: 'bg-gray-400',
          hexColor: t.color || '#6B7280',
          tasks: [],
        }));
        this.columns.push({
          id: 0,
          name: 'sin',
          label: 'Sin tipo',
          color: 'bg-gray-300',
          hexColor: '#D1D5DB',
          tasks: [],
        });
        break;
      case 'category':
        this.columns = this.categories.map((c) => ({
          id: c.id,
          name: c.name,
          label: c.name,
          color: 'bg-gray-400',
          hexColor: c.color || '#6B7280',
          tasks: [],
        }));
        this.columns.push({
          id: 0,
          name: 'sin',
          label: 'Sin categoría',
          color: 'bg-gray-300',
          hexColor: '#D1D5DB',
          tasks: [],
        });
        break;
    }
    this.loadPage(0);
  }

  changeViewMode(mode: ViewMode): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.initializeColumns();
  }

  loadPage(page: number): void {
    if (this.columns.length === 0) return;
    this.loading = true;

    const finalReq: TaskFilterRequest = {
      ...this.currentFilter,
      page,
      size: this.pageSize,
      sortBy: 'datetime_due',
      sortDirection: 'ASC',
    };

    this.columns.forEach((c) => (c.tasks = []));

    this.taskApi
      .filterTasks(finalReq)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          for (const t of resp.content || []) {
            const col = this.findColumnForTask(t);
            if (col) col.tasks.push(t);
          }
          this.globalPage = page;
          this.globalTotalPages = resp.totalPages ?? 0;
          this.globalTotalElements = resp.totalElements ?? 0;
          this.loading = false;
          this.resolveAssociations();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private findColumnForTask(t: TaskResponse): KanbanColumn | undefined {
    let id: number | null | undefined;
    switch (this.viewMode) {
      case 'status':
        id = t.status?.id ?? t.idTaskStatus;
        break;
      case 'priority':
        id = t.priority?.id ?? t.idTaskPriority;
        break;
      case 'type':
        id = t.type?.id ?? t.idTaskType;
        break;
      case 'category':
        id = t.category?.id ?? t.idTaskCategory;
        break;
    }
    if (id == null || id === 0) return this.columns.find((c) => c.id === 0);
    return this.columns.find((c) => c.id === id);
  }

  private resolveAssociations(): void {
    const businessId = this.authService.getCurrentUser()?.businessId ?? 1;
    const companyIds = new Set<number>();
    const dealIds = new Set<number>();
    const leadIds = new Set<number>();

    this.columns.forEach((col) => {
      col.tasks.forEach((t) => {
        if (t.intIdCompany && !this.companyNames.has(t.intIdCompany))
          companyIds.add(t.intIdCompany);
        if (t.intIdDeal && !this.dealNames.has(t.intIdDeal))
          dealIds.add(t.intIdDeal);
        if (t.intIdLead && !this.leadNames.has(t.intIdLead))
          leadIds.add(t.intIdLead);
      });
    });

    if (companyIds.size === 0 && dealIds.size === 0 && leadIds.size === 0)
      return;

    const calls = [
      ...Array.from(companyIds).map((id) =>
        this.empresaApi.getCompanyById(id).pipe(catchError(() => of(null))),
      ),
      ...Array.from(dealIds).map((id) =>
        this.dealApi
          .getDealById(id, businessId)
          .pipe(catchError(() => of(null))),
      ),
      ...Array.from(leadIds).map((id) =>
        this.leadApi
          .getLeadById(id, businessId)
          .pipe(catchError(() => of(null))),
      ),
    ];

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          let i = 0;
          for (const id of companyIds) {
            const c = results[i++] as {
              id?: number;
              commercialName?: string;
            } | null;
            if (c?.commercialName) this.companyNames.set(id, c.commercialName);
          }
          for (const id of dealIds) {
            const d = results[i++] as { id?: number; name?: string } | null;
            if (d?.name) this.dealNames.set(id, d.name);
          }
          for (const id of leadIds) {
            const l = results[i++] as { id?: number; name?: string } | null;
            if (l?.name) this.leadNames.set(id, l.name);
          }
        },
      });
  }

  onFilterChanged(event: TaskFilterEvent): void {
    this.currentFilter = event.request;
    this.quickFilter = event.quickFilter;
    this.loadPage(0);
  }

  // ===== Paginación =====

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    localStorage.setItem(this.PAGE_SIZE_KEY, String(this.pageSize));
    this.loadPage(0);
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.loadPage(page);
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }
  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  // Complete and Cancel Modals
  abrirCancelarDialog(task: TaskResponse): void {
    if (!task.id) return;
    this.cancelTargetTask = task;
    this.showCancelDialog = true;
  }

  cerrarCancelarDialog(): void {
    this.showCancelDialog = false;
    this.cancelTargetTask = null;
  }

  abrirCompletarDialog(task: TaskResponse): void {
    if (!task.id) return;
    this.completeTargetTask = task;
    this.showCompleteDialog = true;
  }

  cerrarCompletarDialog(): void {
    this.showCompleteDialog = false;
    this.completeTargetTask = null;
  }

  aplicarCancelacion(data: {
    taskId: number;
    reasonId: number;
    notes: string;
  }): void {
    this.taskApi
      .cancelTask(data.taskId, {
        idTaskCancelReason: data.reasonId,
        cancelNotes: data.notes,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.showCancelDialog = false;

          this.cancelTargetTask = null;

          this.drawerService.notifySaved(updatedTask);

          this.toast.updated('Tarea');
        },

        error: (err) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo cancelar la tarea.',
          );

          this.toast.error({
            title: 'Error',
            message: msg,
          });
        },
      });
  }

  aplicarCompletar(data: { taskId: number; notes: string }): void {
    this.taskApi
      .completeTask(data.taskId, {
        completionNotes: data.notes.trim(),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.showCompleteDialog = false;
          this.completeTargetTask = null;

          this.drawerService.notifySaved(updatedTask);

          this.toast.updated('Tarea');
        },

        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo completar la tarea.',
          );

          this.toast.error({
            title: 'Error',
            message: msg,
          });
        },
      });
  }

  async reabrir(t: TaskResponse): Promise<void> {
    if (t.id == null) return;
    const reason = await this.confirmDialog.prompt({
      title: 'Reabrir tarea',
      message: 'Indica el motivo por el cual se reabre esta tarea.',
      placeholder: 'Ej. requisito adicional del cliente',
      inputType: 'textarea',
      confirmText: 'Reabrir',
    });
    if (!reason) return;
    this.taskApi
      .reopenTask(t.id, { reason })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedTask) => {
          this.drawerService.notifySaved(updatedTask);
          this.toast.updated('Tarea');
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo reabrir la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  // ===== Drag & drop =====

  drop(event: CdkDragDrop<TaskResponse[]>, target: KanbanColumn): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      return;
    }
    const task = event.previousContainer.data[event.previousIndex];
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex,
    );
    if (task.id == null) return;

    const update: TaskUpdateRequest = {};
    let prevId: number | null | undefined;
    switch (this.viewMode) {
      case 'status':
        prevId = task.status?.id ?? task.idTaskStatus;

        if (target.id === 0 || prevId === target.id) {
          this.revertDrop(task, target, prevId);
          return;
        }

        const targetStatus = this.statuses.find((s) => s.id === target.id);
        const currentStatus = this.statuses.find(
          (s) => s.id === task.idTaskStatus,
        );
        if (currentStatus?.isFinal && !targetStatus?.isFinal) {
          this.reabrir(task);
          this.revertDrop(task, target, prevId);
          return;
        }

        if (targetStatus?.isFinal && targetStatus.isCancelledTerminal) {
          this.abrirCancelarDialog(task);
          this.revertDrop(task, target, prevId);
          return;
        }

        if (targetStatus?.isFinal && targetStatus.isCompletedTerminal) {
          this.abrirCompletarDialog(task);
          this.revertDrop(task, target, prevId);
          return;
        }
        update.idTaskStatus = target.id;
        break;
      case 'priority':
        prevId = task.priority?.id ?? task.idTaskPriority;
        if (prevId === target.id) return;
        update.idTaskPriority = target.id === 0 ? undefined : target.id;
        break;
      case 'type':
        prevId = task.type?.id ?? task.idTaskType;
        if (prevId === target.id) return;
        update.idTaskType = target.id === 0 ? undefined : target.id;
        break;
      case 'category':
        prevId = task.category?.id ?? task.idTaskCategory;
        if (prevId === target.id) return;
        update.idTaskCategory = target.id === 0 ? undefined : target.id;
        break;
    }

    this.taskApi
      .updateTask(task.id, update)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          // Sincroniza ids "planos" para futuros drops
          updated.idTaskStatus = updated.status?.id ?? updated.idTaskStatus;
          updated.idTaskPriority =
            updated.priority?.id ?? updated.idTaskPriority;
          updated.idTaskType = updated.type?.id ?? updated.idTaskType;
          updated.idTaskCategory =
            updated.category?.id ?? updated.idTaskCategory;
          const idx = target.tasks.findIndex((t) => t.id === updated.id);
          if (idx >= 0) target.tasks[idx] = updated;
          this.drawerService.notifySaved(updated);
        },
        error: (err: any) => {
          this.revertDrop(task, target, prevId);
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo cambiar la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  private revertDrop(
    task: TaskResponse,
    target: KanbanColumn,
    prevId: number | null | undefined,
  ): void {
    const idx = target.tasks.findIndex((t) => t.id === task.id);
    if (idx >= 0) {
      const [reverted] = target.tasks.splice(idx, 1);
      const origCol = this.columns.find((c) => c.id === (prevId ?? 0));
      origCol?.tasks.push(reverted);
    }
  }

  getColumnIds(): string[] {
    return this.columns.map((c) => `col-${c.id}`);
  }

  nuevaTarea(): void {
    this.drawerService.open();
  }
  editarTarea(id: number | undefined, ev?: Event): void {
    ev?.stopPropagation();
    if (id != null) this.drawerService.open({ taskId: id });
  }

  abrirDetalle(id: number): void {
    this.detailTaskId = id;
    this.showDetailModal = true;
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.detailTaskId = undefined;
  }

  onDetailChanged(): void {
    this.loadPage(this.globalPage);
  }

  vistaLista(): void {
    this.router.navigate(['/tareas']);
  }
  vistaKanban(): void {
    this.router.navigate(['/tareas/kamban']);
  }
  vistaCalendario(): void {
    this.router.navigate(['/tareas/calendario']);
  }

  // ===== Helpers de visualización =====

  formatDate(iso?: string): string {
    if (!iso) return 'Sin fecha';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'Sin fecha';
    return d.toLocaleDateString();
  }

  // Resolución de catálogos por id (cuando el filter no envía objetos anidados)
  resolveType(t: TaskResponse): TaskTypeResponse | undefined {
    if (t.type?.label) return t.type;
    const id = t.type?.id ?? t.idTaskType;
    return id != null ? this.types.find((x) => x.id === id) : undefined;
  }
  resolveStatus(t: TaskResponse): TaskStatusResponse | undefined {
    if (t.status?.label) return t.status;
    const id = t.status?.id ?? t.idTaskStatus;
    return id != null ? this.statuses.find((x) => x.id === id) : undefined;
  }
  resolvePriority(t: TaskResponse): TaskPriorityResponse | undefined {
    if (t.priority?.label) return t.priority;
    const id = t.priority?.id ?? t.idTaskPriority;
    return id != null ? this.priorities.find((x) => x.id === id) : undefined;
  }
  resolveCategory(t: TaskResponse): TaskCategoryResponse | undefined {
    if (t.category?.name) return t.category;
    const id = t.category?.id ?? t.idTaskCategory;
    return id != null ? this.categories.find((x) => x.id === id) : undefined;
  }

  companyName(id?: number): string | null {
    if (id == null) return null;
    return this.companyNames.get(id) ?? `Empresa #${id}`;
  }
  dealName(id?: number): string | null {
    if (id == null) return null;
    return this.dealNames.get(id) ?? `Negocio #${id}`;
  }
  leadName(id?: number): string | null {
    if (id == null) return null;
    return this.leadNames.get(id) ?? `Lead #${id}`;
  }
  hasAnyAssociation(t: TaskResponse): boolean {
    return !!(t.intIdCompany || t.intIdDeal || t.intIdLead);
  }

  subtaskProgressPercent(t: TaskResponse): number {
    if (!t.subtaskCount || t.subtaskCount === 0) return 0;
    return Math.round((100 * (t.subtaskCompletedCount ?? 0)) / t.subtaskCount);
  }

  isOverdue(t: TaskResponse): boolean {
    if (!t.datetimeDue || t.status?.isFinal) return false;
    return new Date(t.datetimeDue) < new Date();
  }

  private colorClassFor(s: TaskStatusResponse): string {
    const map: Record<string, string> = {
      PENDING: 'bg-slate-500',
      IN_PROGRESS: 'bg-blue-500',
      COMPLETED: 'bg-green-500',
      CANCELLED: 'bg-red-500',
      DEFERRED: 'bg-amber-500',
      WAITING: 'bg-amber-500',
    };
    return map[s.name?.toUpperCase()] || 'bg-gray-400';
  }
}
